// §6.3 / §19.4 — the house dialog: centred, Cancel then OK.
//
// The focus trap is shell-owned and lives in `host/ShellUiHost` — a skin decides what a dialog
// looks like, never whether an app can escape one.
import { useEffect, useRef, useState } from 'react';
import type { DialogProps } from '@hangar/shared';
import { COPY } from '@/copy';

export function Dialog({ request, onAccept, onCancel }: DialogProps) {
  const [value, setValue] = useState(request.defaultValue ?? '');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const acceptRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    setValue(request.defaultValue ?? '');
    if (request.kind === 'prompt') inputRef.current?.focus();
    else acceptRef.current?.focus();
  }, [request.id, request.kind, request.defaultValue]);

  const dismissible = request.kind !== 'alert';

  return (
    <div className="house-dialog" role="dialog" aria-modal="true" data-testid="shell-dialog">
      <p className="house-dialog-message" data-testid="shell-dialog-message">
        {request.message}
      </p>

      {request.kind === 'prompt' ? (
        <input
          ref={inputRef}
          type="text"
          className="house-dialog-input"
          data-testid="shell-dialog-input"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onAccept(value);
          }}
        />
      ) : null}

      <div className="house-dialog-actions">
        {dismissible ? (
          <button
            type="button"
            className="house-dialog-button"
            data-testid="shell-dialog-cancel"
            onClick={onCancel}
          >
            {request.cancelLabel ?? COPY.chrome.cancel}
          </button>
        ) : null}
        <button
          ref={acceptRef}
          type="button"
          className="house-dialog-button house-dialog-button--primary"
          data-testid="shell-dialog-accept"
          onClick={() => onAccept(request.kind === 'prompt' ? value : undefined)}
        >
          {request.confirmLabel ?? COPY.chrome.ok}
        </button>
      </div>
    </div>
  );
}
